// @ts-strict-ignore
import * as React from 'react';
import CommonNotificationSettingsModal from '../sharedComponents/CommonNotificationSettingsModal';
import { hourOfDayFromFormValues, SaveNotificationOverridePayload } from '../../helpers/notificationOverrideHelpers';
import {
    tryUpdateAppSettings,
    tryUpdateAppSettingsV2,
    UpdateNotificationSettingsRequest,
    UpdateStepNotificationConfigsRequest,
} from '../../actions/appSettings';
import { useDispatch, useSelector } from 'react-redux';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import { selectCommonNotificationFromAppNotificationId, selectWorkflowId } from '../../selectors/selectors';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppNotificationType } from '../../types';
import { RunwayUserAction } from '../../helpers/aclHelpers';

interface NotificationSettingsModalProps extends AppComponentProps {
    notificationId: AppNotificationType;
    onClose: () => void;
    onAfterClose: () => void;
    open: boolean;

    initialOverrideTarget?: { stepConfigId?: string; createNew?: boolean };
}

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = (props: NotificationSettingsModalProps) => {
    const dispatch = useDispatch();
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, props.appId));

    const onSubmit = (values: Record<string, unknown>) => {
        const request = {
            orgId: props.orgId,
            appId: props.appId,
            notificationId: props.notificationId,
            ...values,
            hourOfDayForNotification: hourOfDayFromFormValues(values),
        } as UpdateNotificationSettingsRequest;
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateNotificationSetting, request, props.notificationId));
    };

    const onSaveOverride = (payload: SaveNotificationOverridePayload, loadingId: string) => {
        const request: UpdateStepNotificationConfigsRequest = {
            orgId: props.orgId,
            appId: props.appId,
            workflowId,
            notificationId: props.notificationId,
            stepConfigIds: payload.stepConfigIds,
            deletedStepConfigIds: payload.deletedStepConfigIds,
            groupName: payload.groupName,
            enabled: payload.values.enabled as boolean | undefined,
            settings: {
                ...payload.values,
                hourOfDayForNotification: hourOfDayFromFormValues(payload.values),
            } as UpdateStepNotificationConfigsRequest['settings'],
            channels: payload.channels,
            overriddenFields: payload.overriddenFields,
            usePrimaryChannels: payload.usePrimaryChannels,
        };
        dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateStepNotificationConfigs, request, loadingId));
    };

    const onDeleteOverride = (stepConfigIds: string[], loadingId: string) => {
        const request: UpdateStepNotificationConfigsRequest = {
            orgId: props.orgId,
            appId: props.appId,
            workflowId,
            notificationId: props.notificationId,
            stepConfigIds: [],
            deletedStepConfigIds: stepConfigIds,
        };
        dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateStepNotificationConfigs, request, loadingId));
    };

    const notification = useSelector((state: RootState) =>
        selectCommonNotificationFromAppNotificationId(state, props.appId, props.notificationId),
    );

    return (
        <CommonNotificationSettingsModal
            onSubmit={onSubmit}
            actionType={AppSettingsUpdatable.UpdateNotificationSetting}
            notification={notification}
            permissionedActionType={RunwayUserAction.UAUpdateAppSettings}
            stepOverrides={
                workflowId ?
                    {
                        workflowId,
                        overrideActionType: AppSettingsV2Updatable.UpdateStepNotificationConfigs,
                        savedOverrides: notification?.stepConfigOverrides ?? [],
                        onSaveOverride,
                        onDeleteOverride,
                        initialTarget: props.initialOverrideTarget,
                    }
                :   undefined
            }
            {...props}
        />
    );
};

export default NotificationSettingsModal;
